import { fetchSystemInfo } from "@/api/modules/system";
import { fetchSnailmailInfo } from "@/api/modules/delivery";

const SystemConfig = function(config) {
    const self = this;
    self.company = config.company;
    self.trips = config.trips;
    self.stops = config.stops;
    self.payments = config.paymentProviders;
    self.stringCharacterSets = config.stringCharacterSets;
    self.snailmailDelivery = config.snailmailDelivery;

    self.fixedPriceTicketProducts = config.products.fixedPriceTicketProducts;
    self.zoneTicketProducts = config.products.zoneTicketProducts;
    self.retireeTicketProducts = config.products.retireeTicketProducts;
};

export const state = {
    system: null,
    snailmail: null,
    stopProductTypes: []
};

export const mutations = {
    updateSystemConfig: (state, payload) => {
        state.system = new SystemConfig(payload);
    },
    updateSnailmailConfig: (state, payload) => {
        state.snailmail = payload;
    },
    addStopProductType: (state, payload) => {
        state.stopProductTypes.push(payload.productType);
    }
};

export const getters = {
    getMaxStopSearchLength: state => {
        return state.system ? state.system.stops.stopNameMaxLength : null;
    },
    getStopProductTypes: state => {
        return state.stopProductTypes;
    },
    getDibsMerchantId: state => {
        return state.system ? state.system.payments.dibs.merchantId : null;
    },
    getEpayMerchantId: state => {
        return state.system && state.system.payments.epay
            ? state.system.payments.epay.merchantId
            : null;
    },
    getNetaxeptMerchantId: state => {
        return state.system && state.system.payments.netaxept &&
            state.system.payments.netaxept.clientMerchantId
            ? state.system.payments.netaxept.clientMerchantId.webshopMerchantId
            : null;
    },
    getApplePayMerchantId: state => {
        return state.system && state.system.payments.applePay ?
            state.system.payments.applePay.merchantId : null;
    },
    getApplePayMerchantCapabilities: state => {
        return state.system && state.system.payments.applePay ?
            state.system.payments.applePay.merchantCapabilities : null;
    },
    getApplePaySupportedNetworks: state => {
        return state.system && state.system.payments.applePay ?
            state.system.payments.applePay.supportedNetworks : null;
    },
    getCompanyName: state => {
        return state.system.company.companyName;
    },
    getApplePayEnabled: state => {
        return state.system && state.system.payments.applePay.enabled;
    },
    getNetaxeptEnabled: state => {
        return state.system && state.system.payments.netaxept.enabled;
    },
    // helper for mapping a product type identifier to a vuex module
    enabledProductsModuleMapping: (state, getters, rootState) => {
        const mapping = {};
        if (rootState.products) {
            Object.keys(rootState.products).forEach(key => {
                if (rootState.products[key].info)
                    mapping[rootState.products[key].info.identifier] = key;
            });
        }
        return mapping;
    },
    getDepartureInstantThresholdMinutes: state => {
        return state.system && state.system.trips
            ? state.system.trips.orderingDepartureInstantThresholdMinutes
            : 0;
    },
    alphaNumericCharacterSet: state => {
        return (
            state.system &&
            state.system.stringCharacterSets.alphaNumeric.split("")
        );
    },
    commonCharacterSet: state => {
        return (
            state.system && state.system.stringCharacterSets.common.split("")
        );
    },
    mailAddressCharacterSet: state => {
        return (
            state.system &&
            state.system.stringCharacterSets.mailAddress.split("")
        );
    },
    snailmailShipmentDelayDayCount: ({ snailmail }) =>
        snailmail && snailmail.shipmentDelayDayCount,
    snailmailShipmentFeeInOre: ({ snailmail }) =>
        snailmail && snailmail.shipmentFeeInOre
};

export const actions = {
    loadSnailmail: async ({ commit }) => {
        const response = await fetchSnailmailInfo();

        if (!response.ok) {
            throw new Error(JSON.stringify(response.responseBody));
        }

        commit("updateSnailmailConfig", response.responseBody);
    },
    loadConfig: async ({ dispatch, commit, state }) => {
        const response = await fetchSystemInfo();

        if (!response.ok) {
            throw new Error(JSON.stringify(response.responseBody));
        }

        commit("updateSystemConfig", response.responseBody);

        const system = state.system;

        const promises = [
            dispatch("passenger/fetchPassengerTypeList", null, { root: true }),
            dispatch("identification/updateIdentificationTypes", null, {
                root: true
            }),
            dispatch(
                "date/setServerTime",
                { setDepartureDate: true },
                { root: true }
            )
        ];

        if (system.zoneTicketProducts.enabled) {
            promises.push(
                dispatch("products/zoneticket/fetchInfo", null, { root: true })
            );
        }

        if (system.fixedPriceTicketProducts.enabled) {
            promises.push(
                dispatch("products/fixedpriceticket/fetchInfo", null, {
                    root: true
                })
            );
        }

        if (system.retireeTicketProducts.enabled) {
            promises.push(
                dispatch("products/retireeticket/fetchInfo", null, {
                    root: true
                })
            );
        }
        if (system.retireeTicketProducts.enabled || system.controlFeeProducts.enabled) {
            promises.push(
                dispatch("products/controlfee/fetchInfo", null, {
                    root: true
                })
            );
        }

        if (system.snailmailDelivery.enabled) {
            promises.push(dispatch("loadSnailmail"));
        }

        await Promise.all(promises);

        // Only do this if popular stops is enabled
        // We depend on stop types from the above promisses.
        if (process.env.VUE_APP_SHOW_POPULAR_STOP_RESULTS === "true") {
            await dispatch("location/searchPopularStops", null, { root: true });
        }

        return true;
    }
};
